import React, { FunctionComponent, useEffect, useState } from "react";
import { connect } from "react-redux";
import styles from "./area.module.css";
import { Box, Grid, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import { themeColors } from "../../Components/theme";
import "../../theme-vars.css";
import Loading from "../../Components/Loading/Loading";
import InputTextValue from "../../Components/TextInput/InputTextValue";
import InputText from "./InputText";
import DropDownText from "../../Components/DropDownText/DropDownText";
import StyledSubmitButton from "../../Components/Buttons/StyledSubmitButton";

type ConversionItem = {
    unit: string;
    unitVal: number;
};
type props = {
    theme: any;
};

const Converter: FunctionComponent<props> = ({ theme }) => {
    const themeMUI = useTheme();
    const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));
    const [showLoader, setShowLoader] = useState(true);
    const [unitVal, setUnitVal] = useState<number>(0);
    const [unit, setUnit] = useState<{ value: string; label: string }>({ value: "m²", label: "Square Meter (m²)" });
    const [convertedUnitsArr, setConvertedUnitsArr] = useState<ConversionItem[]>([]);
    const [showResult, setShowResult] = useState(false);
    const [message, setMessage] = useState("");

    // All units (display)
    const units: { value: string; label: string }[] = [
        { value: "mm²", label: "Square Millimeter (mm²)" },
        { value: "cm²", label: "Square Centimeter (cm²)" },
        { value: "dm²", label: "Square Decimeter (dm²)" },
        { value: "m²", label: "Square Meter (m²)" },
        { value: "in²", label: "Square Inch (in²)" },
        { value: "ft²", label: "Square Foot (ft²)" },
        { value: "yd²", label: "Square Yard (yd²)" },
        { value: "acre", label: "Acre" },
        { value: "a", label: "Are (a)" },
        { value: "ha", label: "Hectare (ha)" },
        { value: "km²", label: "Square Kilometer (km²)" },
        { value: "Gunta", label: "Gunta" },
        { value: "Cent (dismil)", label: "Cent (dismil)" },
        { value: "Bigha (Kaccha)", label: "Bigha (Kaccha)" },
        { value: "Bigha (Gujarat)", label: "Bigha (Gujarat)" },
    ];

    // Conversion factors relative to 1 m²
    const factors: Record<string, number> = {
        "mm²": 1e6,
        "cm²": 1e4,
        "dm²": 100,
        "m²": 1,
        "in²": 1550.0031,
        "ft²": 10.7639,
        "yd²": 1.19599,
        acre: 0.000247105,
        a: 0.01, // are
        ha: 0.0001,
        "km²": 0.000001,
        Gunta: 0.02471,
        "Cent (dismil)": 0.02471,
        "Bigha (Kaccha)": 0.001488, // approx, varies by region
        "Bigha (Gujarat)": 0.00025, // approx, varies by region
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowLoader(false);
        }, 500);
        return () => clearTimeout(timer);
    }, []);

    const calculateUnits = (num: number, fromUnit: string) => {
        if (!factors[fromUnit]) return setConvertedUnitsArr([]);
        const valueInM2 = num / factors[fromUnit]; // convert to m²
        const convertedUnits: ConversionItem[] = Object.keys(factors).map((u) => ({
            unit: u,
            unitVal: parseFloat((valueInM2 * factors[u]).toFixed(8)),
        }));
        setConvertedUnitsArr(convertedUnits);
    };

    const handleConvert = () => {
        if (!unitVal || unitVal === 0) {
            setShowResult(false);
            setMessage("Please enter a valid value.");
            setConvertedUnitsArr([]);
            return;
        }
        calculateUnits(unitVal, unit.value);
        setShowResult(true);
        setMessage("Conversion complete!");
    };

    const handleUnitChange = (e: any) => {
        const selectedUnit = units.find((u) => u.value === e.target.value);
        if (selectedUnit) {
            setUnit(selectedUnit);
        }
    };

    const containerStyles = {
        backgroundColor: theme
            ? themeColors.backgroundColor
            : themeColors.backgroundColorDark,
        color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
    };

    const cardContainerStyles = {
        backgroundColor: theme
            ? themeColors.cardBackgroundColor
            : themeColors.cardBackgroundColorDark,
        color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
    };

    return (
        <>
            <Box
                className={styles.parent}
                sx={{
                    ...containerStyles,
                    marginTop: isMobile ? "6%" : "4%",
                    overflow: "hidden",
                    maxHeight: "500px",
                    overflowX: "hidden",
                    overflowY: "scroll",
                    marginBottom: "10px",
                }}
            >
                {showLoader && <Loading />}
                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "flex-start",
                        flexDirection: "column",
                        padding: isMobile ? "20px 10px" : "5px 20px",
                    }}
                >
                    {isMobile && (
                        <h4
                            style={{
                                fontFamily: "var(--font-family-main)",
                                fontSize: "var(--font-size-large)",
                                margin: "0px 0 12px 0",
                            }}
                        >
                            Area Converter
                        </h4>
                    )}
                    <Box
                        sx={{
                            ...cardContainerStyles,
                            boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px",
                            width: "100%",
                            borderRadius: "10px",
                            justifyContent: "flex-start",
                            alignItems: "flex-start",
                            height: "auto",
                            paddingBottom: "50px",
                            marginBottom: "50px",
                            padding: "20px 20px 20px 20px",
                        }}
                    >
                        {/* Input & Dropdown */}
                        <Box sx={{ display: "flex", gap: 1, mb: 3, width: '100%' }}>
                            <Grid container spacing={isMobile ? 2 : 3} sx={{ margin: 0 }}>
                                <Grid item xs={12} sm={5}>
                                    <InputText
                                        title="Value"
                                        value={unitVal === 0 ? "" : unitVal}
                                        onChange={(e) => setUnitVal(Number(e.target.value))}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                    <DropDownText
                                        title="Units"
                                        options={units}
                                        value={unit.value === "Select" ? "" : unit.value}
                                        onChange={handleUnitChange}
                                        required
                                        labelKey="label"
                                        valueKey="value"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
                                    <Box>
                                        <StyledSubmitButton
                                            onClick={handleConvert}
                                            themeMode={themeMUI.palette.mode}
                                            isMobile={isMobile}
                                        >
                                            Convert
                                        </StyledSubmitButton>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                        {/* Message */}
                        {showResult && (
                            <Typography
                                variant="subtitle1"
                                color={
                                    message === "Conversion complete!" ? "success.main" : "error.main"
                                }
                                sx={{ mb: 2 }}
                            >
                                {message}
                            </Typography>
                        )}
                        {/* Results */}
                        {showResult && convertedUnitsArr.length > 0 && (
                            <Paper elevation={2} sx={{ ...cardContainerStyles, borderRadius: 2, overflow: "hidden" }}>
                                {convertedUnitsArr.map((item, index) => {
                                    const unitObj = units.find(u => u.value === item.unit);
                                    return (
                                        <Box
                                            key={index}
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                px: 2,
                                                py: 1,
                                                borderBottom:
                                                    index !== convertedUnitsArr.length - 1
                                                        ? "1px solid #eee"
                                                        : "none",
                                            }}
                                        >
                                            <span>{item.unitVal}</span>
                                            <span>{unitObj ? unitObj.label : item.unit}</span>
                                        </Box>
                                    );
                                })}
                            </Paper>
                        )}
                    </Box>
                </Box>
            </Box>
        </>
    );
};

const mapStateToProps = (state: any) => {
    return {
        theme: state.theme.isLightMode,
    };
};
export default connect(mapStateToProps)(Converter);
